import background from "@assets/bg_player.jpg";
import { Box, Card, Container, Stack } from "@mui/material";
import { alpha } from "@mui/material/styles";
import type React from "react";

interface FullPageLayoutProps {
	children: React.ReactNode;
	maxWidth?: "xs" | "sm" | "md" | "lg" | "xl";
}

/**
 * Reusable full-page layout with background image and centered card.
 * Used for login, reset-password, and similar standalone pages.
 */
export const FullPageLayout: React.FC<FullPageLayoutProps> = ({
	children,
	maxWidth = "sm",
}) => (
	<Box
		sx={{
			position: "relative",
			minHeight: "100dvh",
			display: "flex",
			alignItems: "center",
			justifyContent: "center",
			p: 2,
		}}
	>
		{/* Fixed background layer for reliable mobile rendering */}
		<Box
			aria-hidden={true}
			sx={{
				position: "fixed",
				inset: 0,
				zIndex: 0,
				backgroundImage: `url(${background})`,
				backgroundRepeat: "no-repeat",
				backgroundPosition: "center center",
				backgroundSize: "cover",
				WebkitTransform: "translateZ(0)",
			}}
		/>
		<Container
			maxWidth={maxWidth}
			sx={{
				display: "flex",
				justifyContent: "center",
				position: "relative",
				zIndex: 1,
			}}
		>
			<Card
				elevation={8}
				sx={{
					width: "100%",
					maxWidth: 520,
					p: { xs: 3, sm: 4 },
					bgcolor: (theme) => alpha(theme.palette.background.paper, 0.85),
					backdropFilter: "blur(6px)",
					borderRadius: 3,
				}}
			>
				<Stack spacing={3} alignItems="center">
					{children}
				</Stack>
			</Card>
		</Container>
	</Box>
);
